import arrayUnique from 'array-unique';
import pMap from 'p-map';

import { Logger } from '@gitbook/util-debug';
import { deepEquals } from '@gitbook/util-immutable';
import {
    defaultMergeConflictsStrategy,
    type MergeStrategy,
    mergeLeafsProperty,
    mergeMaps,
    mergeTrees,
} from '@gitbook/util-tree-merge';

import { compareFiles } from '../files';
import { comparePages } from '../pages';
import {
    type GitFileState,
    type Revision,
    RevisionConflictState,
    type RevisionConflicts,
    type RevisionPage,
    type RevisionPageDocument,
    RevisionPageType,
    type RevisionReusableContents,
    type RevisionTags,
    RevisionType,
    type RevisionVariables,
} from '../types';
import { mergeEdits, randomUUID } from '../utils';
import { createFastForwardRevision } from './createFastForwardRevision';
import { createTreePages, flattenTreePages, type RevisionTreePage } from './diffRevisionPages';

const logger = Logger('hive:mergeRevisions');

interface MergeRevisionsOptions {
    type: RevisionType.Merge | RevisionType.Update;
    handleDocumentsConflict: (
        pageId: string,
        ancestorDocumentId: string | undefined,
        baseDocumentId: string,
        headDocumentId: string | undefined
    ) => Promise<{
        documentId: string | undefined;
        conflicts: boolean;
    }>;
}

/**
 * 3-way merge for revisions.
 * This method is used when merging a change request.
 */
export async function mergeRevisions(
    revisions: {
        ancestor: Revision;
        head: Revision;
        base: Revision;
    },
    options: MergeRevisionsOptions
): Promise<Revision> {
    const { ancestor, head, base } = revisions;

    // Fast forward
    if (ancestor.id === base.id) {
        logger.info(`fast forward with head ${head.id}`);
        return createFastForwardRevision(head, ancestor, options.type);
    }
    if (ancestor.id === head.id) {
        logger.info(`fast forward with base ${base.id}`);
        return createFastForwardRevision(base, ancestor, options.type);
    }

    logger.info(`3-way merge between: ${ancestor.id}/${head.id}/${base.id}`);

    const conflictingDocuments = await mergePageDocuments(ancestor, head, base, options);

    const ancestorPages = createTreePages(ancestor.pages, ancestor.rootPages);
    const basePages = createTreePages(base.pages, base.rootPages);
    const headPages = createTreePages(head.pages, head.rootPages);

    const strategy: MergeStrategy<RevisionTreePage> = {
        getId: (node) => node.renderKey,
        getChildren: (node) => node.pages,
        setChildren: (node, pages) => ({
            ...node,
            pages,
        }),
        compareItems: (original, modified) => (comparePages(original.page, modified.page) ? 1 : 0),
        mergeConflicts: (mergeCase) => {
            switch (mergeCase.case) {
                case 'conflict-both-modified': {
                    const conflictingDocument = conflictingDocuments.get(mergeCase.head.page.id);

                    let documentId: string | undefined;

                    // If we are merging conflicts between documents, infer the documentId from either
                    // the conflict resolution step or by using the base/head/ancestor properties.
                    if (
                        mergeCase.base.page.type === RevisionPageType.Document &&
                        mergeCase.head.page.type === RevisionPageType.Document &&
                        (!mergeCase.ancestor ||
                            mergeCase.ancestor.page.type === RevisionPageType.Document)
                    ) {
                        const ancestorPage = mergeCase.ancestor?.page as
                            | RevisionPageDocument
                            | undefined;

                        documentId = conflictingDocument
                            ? conflictingDocument.documentId
                            : mergeLeafsProperty(
                                  'document',
                                  ancestorPage,
                                  mergeCase.base.page,
                                  mergeCase.head.page,
                                  {
                                      // Once a document is set on a page it should not be removed by merge.
                                      // Missing/undefined here indicates invalid upstream state, not an optional deletion.
                                      isRemovable: false,
                                  }
                              );
                    }

                    const page = mergePages(
                        mergeCase.ancestor ? mergeCase.ancestor.page : null,
                        mergeCase.head.page,
                        mergeCase.base.page,
                        documentId
                    );

                    return {
                        items: [{ renderKey: page.id, page, pages: mergeCase.children }],
                    };
                }
                default:
                    return defaultMergeConflictsStrategy(mergeCase, strategy);
            }
        },
    };

    const result = mergeTrees(
        {
            ancestor: ancestorPages,
            head: headPages,
            base: basePages,
        },
        strategy
    );

    // List pages with conflicts to resolve
    const conflictingPageEntries: RevisionConflicts['pages'] = {};

    // Conflicting documents mean that the page didn't change, but the content of the page did.
    Array.from(conflictingDocuments.entries())
        .filter(([, state]) => state.conflicts)
        .forEach(([pageId]) => {
            conflictingPageEntries[pageId] = RevisionConflictState.DocumentConflict;
        });

    // otherwise identify the conflicts where a page was removed on one side but modified on the other
    result.changes.forEach((change, key) => {
        switch (change.case) {
            case 'conflict-deleted-in-base': {
                if (change.isHeadModified) {
                    conflictingPageEntries[key] = RevisionConflictState.DeletedInBase;
                } else {
                    conflictingPageEntries[key] =
                        RevisionConflictState.DeletedInBaseSubtreeModifiedInHead;
                }
                break;
            }
            case 'conflict-deleted-in-head': {
                if (change.isBaseModified) {
                    conflictingPageEntries[key] = RevisionConflictState.DeletedInHead;
                } else {
                    conflictingPageEntries[key] =
                        RevisionConflictState.DeletedInHeadSubtreeModifiedInBase;
                }
                break;
            }
        }
    });

    const revision: Revision = {
        id: randomUUID(),
        type: options.type,
        contentId: ancestor.contentId,
        parents: [ancestor.id, base.id, head.id],
        files: mergeFiles(ancestor.files, head.files, base.files),
        variables: mergeVariables(ancestor.variables, head.variables, base.variables),
        reusableContents: mergeReusableContents(
            ancestor.reusableContents ?? {},
            head.reusableContents ?? {},
            base.reusableContents ?? {}
        ),
        tags: mergeTags(ancestor.tags, head.tags, base.tags),
        ...flattenTreePages(result.items),

        // When updating a branch, we don't consider that the contributors to the main branch have edited the content.
        edits:
            options.type === RevisionType.Update ? head.edits : mergeEdits(base.edits, head.edits),
    };

    if (Object.keys(conflictingPageEntries).length > 0) {
        logger.info('merge had conflicts');
        revision.conflicts = {
            pages: conflictingPageEntries,
        };
    } else {
        logger.info('merge without conflicts');
    }

    return revision;
}

/**
 * 3-way merge for pages.
 */
function mergePages(
    ancestor: RevisionPage | null,
    head: RevisionPage,
    base: RevisionPage,
    resolvedDocument: string | undefined
): RevisionPage {
    const title = mergeLeafsProperty('title', ancestor, base, head, { isRemovable: false });
    const emoji = mergeLeafsProperty('emoji', ancestor, base, head, { isRemovable: true });
    const icon = mergeLeafsProperty('icon', ancestor, base, head, { isRemovable: true });

    const iconEmoji = icon ? { icon, emoji: undefined } : { emoji, icon: undefined };

    const git = mergeGitFileState(ancestor, base, head);

    switch (head.type) {
        case RevisionPageType.Document:
            if (
                base.type !== RevisionPageType.Document ||
                (ancestor && ancestor.type !== RevisionPageType.Document)
            ) {
                logger.debug(
                    `ancestor/head/base with key ${head.id} have kind: ${ancestor?.type}/${head.type}/${base.type}`
                );
                throw new Error(`pages with same key ${head.id} has different kind`);
            }
            return {
                ...head,
                ...iconEmoji,
                git,
                title,
                slug: mergeLeafsProperty('slug', ancestor, base, head, {
                    isRemovable: true,
                }),
                cover: mergeLeafsProperty('cover', ancestor, base, head, {
                    isRemovable: true,
                }),
                layout: mergeLeafsProperty('layout', ancestor, base, head, {
                    isRemovable: true,
                }),
                hidden: mergeLeafsProperty('hidden', ancestor, base, head, {
                    isRemovable: true,
                }),
                document: resolvedDocument,
            };
        case RevisionPageType.Link:
            if (
                base.type !== RevisionPageType.Link ||
                (ancestor && ancestor.type !== RevisionPageType.Link)
            ) {
                logger.debug(
                    `ancestor/head/base with key ${head.id} have kind: ${ancestor?.type}/${head.type}/${base.type}`
                );
                throw new Error(`pages with same key ${head.id} has different kind`);
            }
            return {
                ...head,
                ...iconEmoji,
                git,
                title,
                target: mergeLeafsProperty('target', ancestor, base, head, {
                    isRemovable: false,
                }),
            };
        case RevisionPageType.Computed:
            if (
                base.type !== RevisionPageType.Computed ||
                (ancestor && ancestor.type !== RevisionPageType.Computed)
            ) {
                logger.debug(
                    `ancestor/head/base with key ${head.id} have kind: ${ancestor?.type}/${head.type}/${base.type}`
                );
                throw new Error(`pages with same key ${head.id} has different kind`);
            }
            return {
                ...head,
                ...iconEmoji,
                git,
                title,
                computed: mergeLeafsProperty('computed', ancestor, base, head, {
                    isRemovable: false,
                }),
            };
        case RevisionPageType.Group:
            if (
                base.type !== RevisionPageType.Group ||
                (ancestor && ancestor.type !== RevisionPageType.Group)
            ) {
                logger.debug(
                    `ancestor/head/base with key ${head.id} have kind: ${ancestor?.type}/${head.type}/${base.type}`
                );
                throw new Error(`pages with same key ${head.id} has different kind`);
            }
            return {
                ...head,
                ...iconEmoji,
                git,
                slug: mergeLeafsProperty('slug', ancestor, base, head, {
                    isRemovable: true,
                }),
                title,
                hidden: mergeLeafsProperty('hidden', ancestor, base, head, {
                    isRemovable: true,
                }),
            };
    }
}

/**
 * Return true if two versions of a page, file or reusable content are at a different location in the
 * repository.
 *
 * Only the location counts. The blob `oid` identifies the content living at that location, so it
 * also changes when a branch edits the entity and syncs it with Git. Comparing it here would read
 * such an edit as a move and let the branch impose the location it was forked from.
 */
function hasGitFileMoved<T extends { git?: GitFileState }>(a: T, b: T): boolean {
    return a.git?.filePath !== b.git?.filePath;
}

/**
 * 3-way merge the Git state of a page, file or reusable content.
 *
 * The state records where the entity lives in the repository, so a branch that never moved it must
 * adopt the other branch's location: keeping HEAD's location unconditionally resurrects the path a
 * stale change request was forked from, and the export then moves the file back there. The `oid`
 * travels with whichever side owns the resulting path, since it describes the blob living there.
 */
function mergeGitFileState<T extends { git?: GitFileState }>(
    ancestor: T | null | undefined,
    base: T,
    head: T
): GitFileState | undefined {
    // Without an ancestor we cannot tell a move from an unchanged location, so HEAD wins.
    if (!ancestor) {
        return head.git;
    }

    return hasGitFileMoved(ancestor, head) ? head.git : base.git;
}

/**
 * Merge files. Conflicts for files cannot be handled yet.
 */
function mergeFiles(
    ancestor: Revision['files'],
    head: Revision['files'],
    base: Revision['files']
): Revision['files'] {
    return mergeMaps(
        {
            ancestor,
            head,
            base,
        },
        {
            // `compareFiles` compares content only, but a file that moved on one side must still be
            // seen as modified here, otherwise the merge below is skipped and the other side's
            // location is kept. This stays local to the merge: elsewhere a move is deliberately not
            // a content change, so it does not surface in the change request diffs.
            compareItems: (original, other) =>
                compareFiles(original, other) || hasGitFileMoved(original, other) ? 1 : 0,
            mergeItems: (mergeCase) => {
                return {
                    ...mergeCase.head,
                    git: mergeGitFileState(mergeCase.ancestor, mergeCase.base, mergeCase.head),
                    edits: mergeEdits(mergeCase.base.edits, mergeCase.head.edits),
                    name: mergeLeafsProperty(
                        'name',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                    downloadURL: mergeLeafsProperty(
                        'downloadURL',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                    size: mergeLeafsProperty(
                        'size',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                    dimensions: mergeLeafsProperty(
                        'dimensions',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                    blobId: mergeLeafsProperty(
                        'blobId',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                };
            },
        }
    );
}

/**
 * Merge page or revision variables.
 */
function mergeVariables(
    ancestor: RevisionVariables | undefined,
    head: RevisionVariables | undefined,
    base: RevisionVariables | undefined
): RevisionVariables | undefined {
    if (!ancestor && !head && !base) {
        return undefined;
    }

    return mergeMaps(
        {
            ancestor: ancestor ?? {},
            head: head ?? {},
            base: base ?? {},
        },
        {
            compareItems: (original, other) => (deepEquals(original, other) ? 0 : 1),
            mergeItems: (mergeCase) => {
                return mergeCase.head;
            },
        }
    );
}

/**
 * Merge reusable content. Conflicts are not handled yet - HEAD document is always taken.
 */
function mergeReusableContents(
    ancestor: RevisionReusableContents,
    head: RevisionReusableContents,
    base: RevisionReusableContents
): RevisionReusableContents {
    return mergeMaps(
        {
            ancestor,
            head,
            base,
        },
        {
            compareItems: (original, other) => (deepEquals(original, other) ? 0 : 1),
            mergeItems: (mergeCase) => {
                return {
                    ...mergeCase.head,
                    git: mergeGitFileState(mergeCase.ancestor, mergeCase.base, mergeCase.head),
                    edits: mergeEdits(mergeCase.base.edits, mergeCase.head.edits),
                    title: mergeLeafsProperty(
                        'title',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                };
            },
        }
    );
}

/**
 * Merge tags using 3-way merge. When both sides modify a tag's label, HEAD wins.
 */
function mergeTags(
    ancestor: RevisionTags | undefined,
    head: RevisionTags | undefined,
    base: RevisionTags | undefined
): RevisionTags {
    // Missing slugs must not resolve to inherited properties such as constructor.
    return mergeMaps(
        {
            ancestor: Object.assign(Object.create(null), ancestor),
            head: Object.assign(Object.create(null), head),
            base: Object.assign(Object.create(null), base),
        },
        {
            compareItems: (original, other) => (deepEquals(original, other) ? 0 : 1),
            mergeItems: (mergeCase) => {
                const emoji = mergeLeafsProperty(
                    'emoji',
                    mergeCase.ancestor,
                    mergeCase.base,
                    mergeCase.head,
                    { isRemovable: true }
                );
                const icon = mergeLeafsProperty(
                    'icon',
                    mergeCase.ancestor,
                    mergeCase.base,
                    mergeCase.head,
                    { isRemovable: true }
                );
                const iconEmoji = icon ? { icon, emoji: undefined } : { emoji, icon: undefined };

                return {
                    ...mergeCase.head,
                    edits: mergeEdits(mergeCase.base.edits, mergeCase.head.edits),
                    label: mergeLeafsProperty(
                        'label',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                    ...iconEmoji,
                    color: mergeLeafsProperty(
                        'color',
                        mergeCase.ancestor,
                        mergeCase.base,
                        mergeCase.head,
                        { isRemovable: false }
                    ),
                };
            },
        }
    );
}

/**
 * Merge all documents that have been edited on both side and return a map to resolve those.
 */
async function mergePageDocuments(
    ancestor: Revision,
    head: Revision,
    base: Revision,
    options: MergeRevisionsOptions
) {
    const conflictingDocuments = new Map<
        string,
        {
            documentId: string | undefined;
            conflicts: boolean;
        }
    >();

    const allPageKeys = arrayUnique([...Object.keys(base.pages), ...Object.keys(head.pages)]);

    await pMap(
        allPageKeys,
        async (pageId) => {
            const basePage = base.pages[pageId];
            const headPage = head.pages[pageId];
            const ancestorPage = ancestor.pages[pageId];
            if (
                headPage &&
                basePage &&
                ancestorPage &&
                headPage.type === RevisionPageType.Document &&
                basePage.type === RevisionPageType.Document &&
                ancestorPage.type === RevisionPageType.Document &&
                // page has been modified in base
                basePage.document &&
                basePage.document !== ancestorPage.document &&
                // and also modified in head
                headPage.document &&
                headPage.document !== ancestorPage.document
            ) {
                conflictingDocuments.set(
                    pageId,
                    await options.handleDocumentsConflict(
                        pageId,
                        ancestorPage.document,
                        basePage.document,
                        headPage.document
                    )
                );
            }
        },
        {
            concurrency: 10,
        }
    );

    return conflictingDocuments;
}
